/**
 * S1 Roster & directory (+ S2 attributes at VIEW-ONLY).
 *
 * Reads lexserver's platform-admin family (`lib/specialists-api.ts`). NOT the
 * runtime fs/git proxy: this is a PLATFORM-OWNED share, a different origin and
 * auth scope. Rendering idiom is reused from the runtime browsers; transport is
 * not — that distinction is the whole reason this section could be planned
 * before the API existed.
 *
 * S2 is view-only by agreement: the attributes EDIT half is decoupled from this
 * handback and follows the override-store mechanism decision, which is not
 * portal's to make.
 */

import { AlertTriangle, HelpCircle, RefreshCw, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { isDegraded, renderColumn } from "@/lib/specialist-column";
import { describeProvenance } from "@/lib/specialist-provenance";
import {
  describeSpecialistsError,
  getSpecialists,
  SpecialistsApiError,
  type SpecialistRow,
  type SpecialistsResult,
} from "@/lib/specialists-api";

/** One cell that refuses to turn "unknown" into "empty". */
function Cell({
  col,
  children,
  testid,
}: {
  col: { status: string; reason?: string } | null | undefined;
  children: React.ReactNode;
  testid?: string;
}) {
  const r = renderColumn(col);
  if (r.kind === "value") return <>{children}</>;
  return (
    <span
      className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-500"
      title={r.reason}
      data-testid={testid}
    >
      <HelpCircle className="h-3 w-3 shrink-0" />
      unknown
    </span>
  );
}

export function SpecialistRoster() {
  const [data, setData] = useState<SpecialistsResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getSpecialists());
      setError(null);
    } catch (e) {
      // Deliberately NOT clearing `data`: a failed refresh should leave the last
      // good read on screen with the error above it, rather than blanking the
      // table into something that looks like "no specialists".
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: SpecialistRow[] = useMemo(() => data?.rows ?? [], [data]);
  // Pinned per render pass rather than read per row, so every age on screen is
  // measured against ONE instant — rows that differ by a tick otherwise sort
  // and read as though they were reported at different times.
  const now = Date.now();
  const neverLoaded = data === null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="specialists-roster">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
        {data && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            home <span className="font-mono">{data.home.state}</span>
            {data.home.roster ? ` · ${data.home.roster.length} in roster` : ""}
          </span>
        )}
      </div>

      {error && (
        <div
          className="shrink-0 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="specialists-error"
        >
          {error instanceof SpecialistsApiError ? describeSpecialistsError(error) : error.message}
        </div>
      )}

      {/* The SERVER's own verdict, not a count we re-derived. */}
      {isDegraded(data) && (
        <div
          className="shrink-0 rounded-md bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
          data-testid="specialists-degraded"
        >
          <span className="font-medium">Incomplete view</span> — at least one column below could
          not be determined. Cells marked <span className="font-medium">unknown</span> carry the
          reason; they are NOT empty values.
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Users className="h-4 w-4" />
            {neverLoaded
              ? loading
                ? "Loading…"
                : "Roster — could not read"
              : `${rows.length} specialist${rows.length === 1 ? "" : "s"}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-auto p-0">
          {neverLoaded && !loading && (
            <p
              className="py-10 text-center text-sm text-amber-600 dark:text-amber-500"
              data-testid="specialists-unreadable"
            >
              Could not read the specialists roster. This is NOT “no specialists”.
            </p>
          )}
          {!neverLoaded && rows.length === 0 && (
            <p className="py-10 text-center text-sm text-slate-400" data-testid="specialists-empty">
              The platform has no specialists registered.
            </p>
          )}
          {rows.length > 0 && (
            <table className="w-full text-left text-xs" data-testid="specialists-table">
              <thead className="sticky top-0 bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Specialist</th>
                  <th className="px-3 py-2 font-medium">Domain</th>
                  <th className="px-3 py-2 font-medium">Practice area</th>
                  <th className="px-3 py-2 font-medium">Poller</th>
                  <th className="px-3 py-2 font-medium">Prompt</th>
                  <th className="px-3 py-2 text-right font-medium">kb / mem / wiki</th>
                  <th className="px-3 py-2 font-medium">Apply mode</th>
                  {/* The other half of the bargain that kept `reportedAt`/
                      `reportedBy` as siblings of `values`: "how stale" is the
                      first question about a `known` attribute, and a field you
                      had protected and never render is worse than one you never
                      asked for. NO staleness verdict — see the module. */}
                  <th className="px-3 py-2 font-medium">Reported</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.specialistId} data-testid={`specialists-row-${r.specialistId}`}>
                    <td className="px-3 py-2 font-mono">
                      {r.specialistId}
                      {!r.inRoster && (
                        <span
                          className="ml-1 inline-flex items-center gap-1 rounded bg-amber-50 px-1 text-[10px] uppercase text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
                          title="Registered on the platform but absent from the home roster."
                        >
                          <AlertTriangle className="h-3 w-3" />
                          not in roster
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Cell col={r.attributes} testid={`specialists-domain-unknown-${r.specialistId}`}>
                        {r.attributes.status === "known" ? (r.attributes.values.domain ?? "—") : null}
                      </Cell>
                    </td>
                    <td className="px-3 py-2">
                      <Cell col={r.attributes}>
                        {r.attributes.status === "known"
                          ? (r.attributes.values.practiceArea ?? "—")
                          : null}
                      </Cell>
                    </td>
                    <td className="px-3 py-2">
                      {/* `pollerOptIn` is the WIRE name and deliberately NOT the
                          manifest's (`bus.servable` since `9605c3973`) — see the
                          DO-NOT-RENAME note on the field in `lib/specialists-api.ts`.
                          Renaming it here typechecks and silently sends every row
                          down the `: "out"` branch below. */}
                      <Cell col={r.attributes}>
                        {r.attributes.status === "known"
                          ? r.attributes.values.pollerOptIn
                            ? "opted in"
                            : "out"
                          : null}
                      </Cell>
                    </td>
                    <td className="px-3 py-2">
                      <Cell col={r.prompt}>
                        {r.prompt.status === "known" ? (
                          <span className="font-mono" title={r.prompt.row.slug}>
                            v{r.prompt.row.productionVersion ?? "?"}
                            {r.prompt.row.disabled && (
                              <span className="ml-1 text-amber-600 dark:text-amber-500">disabled</span>
                            )}
                            {!r.prompt.row.present && (
                              <span className="ml-1 text-red-600 dark:text-red-400">absent</span>
                            )}
                          </span>
                        ) : null}
                      </Cell>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <Cell col={r.workspace}>
                        {r.workspace.status === "known"
                          ? `${r.workspace.stats.kbDocs} / ${r.workspace.stats.memoryDocs} / ${r.workspace.stats.wikiDocs}`
                          : null}
                      </Cell>
                    </td>
                    <td className="px-3 py-2">
                      <Cell col={r.applyMode}>
                        {r.applyMode.status === "known" ? r.applyMode.mode : null}
                      </Cell>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {(() => {
                        const p = describeProvenance(r.attributes, now);
                        // `none` means the attributes column is itself unknown —
                        // that cell already carries the server's reason, and a
                        // second marker beside it would just be noise.
                        if (p.kind === "none") return null;
                        return (
                          <span
                            className="text-slate-500 dark:text-slate-400"
                            title={p.detail}
                            data-testid={`specialists-reported-${r.specialistId}`}
                          >
                            {p.age}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
